import React, { FC, useEffect, useState } from "react";
import cx from "classnames";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import styles from "./car-card-galery.module.scss";
import ActionButton, { ActionButtonPrefetchedData } from "../../vdp-buttons/actionButton";
import CarCardImageButton, { ArrowDirection } from "../car-card-image-button";
import { getTranslationModule, Translations } from "../../../../utils/translations";
import { Vehicle } from "@/types/vehicle";
import { VehicleSpecialColor } from "@/types/blocks";
import Discount from "../discount/discount";

export type CarCard = "row" | "grid";

interface ICarCardGalery {
  photos?: string[];
  dealerPhoto?: string;
  vehicle: Vehicle;
  translations: Translations;
  onVehicleDataChanged: () => void;
  vehicleClickHandler: () => void;
  underImagesActionButtons: ActionButtonPrefetchedData[];
  carCardType: CarCard;
  hasDiscount?: boolean;
  specialText?: string;
  specialColor?: VehicleSpecialColor;
}

const CarCardGalery: FC<ICarCardGalery> = ({
  photos,
  dealerPhoto,
  vehicle,
  translations,
  onVehicleDataChanged,
  vehicleClickHandler,
  underImagesActionButtons,
  carCardType,
  hasDiscount,
  specialText,
  specialColor,
}) => {
  const t_common = getTranslationModule(translations, "common");

  const handleOnImageLoadError = (e: any, item: Vehicle) => {
    e.target.src = `/images/brands/${item?.make.toLowerCase()}.png` || "/images/out-of-stock.png";
  };

  const [swipeEnabled, setSwipeEnabled] = useState(true);
  const [touchEventsSet, setTouchEventsSet] = useState(false);
  const swipeScrollTolerance = 30;
  const allowSwipe = carCardType === "grid";

  useEffect(() => {
    if (allowSwipe && photos?.length) {
      const disableScroll = () => {
        let carousel = document.querySelector(".carouselSlider");

        let startPos: any;
        let endPos;
        let isScrolling;
        let passedSwipeScrollTolerance = false;

        function touchMove(e: any) {
          if (e.targetTouches.length > 1 || (e.scale && e.scale !== 1) || !startPos) {
            return;
          }

          if (!passedSwipeScrollTolerance && swipeEnabled) {
            const touch = e.targetTouches[0];
            endPos = {
              x: Math.abs(touch.pageX - startPos.x),
              y: Math.abs(touch.pageY - startPos.y),
            };
            passedSwipeScrollTolerance = endPos.x > swipeScrollTolerance;

            isScrolling = endPos.x < endPos.y; //When isScrolling it means vertical sliding
            if (isScrolling) {
              setSwipeEnabled(false);
            }
          }
        }

        function touchEnd() {
          setSwipeEnabled(true);
          passedSwipeScrollTolerance = false;
          carousel && carousel.removeEventListener("touchmove", touchMove);
        }

        function touchStart(e: any) {
          const touch = e.targetTouches[0]; //The touches array object gets all the touches on the screen, taking the first touch
          startPos = {
            x: touch.pageX,
            y: touch.pageY,
          };

          carousel && carousel.addEventListener("touchmove", touchMove);
        }

        if (!touchEventsSet) {
          carousel && carousel.addEventListener("touchstart", touchStart);
          carousel && carousel.addEventListener("touchend", touchEnd);
          setTouchEventsSet(true);
        }

        if (!swipeEnabled) {
          setSwipeEnabled(true);
          passedSwipeScrollTolerance = false;
        }
      };

      disableScroll();
    }
  }, [photos, swipeEnabled, touchEventsSet, allowSwipe]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState(new Set());

  const markAsLoaded = (index: number) => {
    setLoadedSlides((prev) => new Set(prev).add(index));
  };

  return (
    <div
      className={cx({
        [styles.galeryLogo]: !photos?.length,
        [styles.galeryRow]: carCardType === "row",
        [styles.galeryGrid]: carCardType === "grid",
      })}
    >
      {underImagesActionButtons?.length > 0 && (
        <div className={"srpVideoButtonContainer"}>
          {underImagesActionButtons.map((item, index) => (
            <ActionButton
              key={index}
              prfetchedData={item.prefetchedValue}
              className={styles.button}
              actionButton={item.actionButtonData}
              currentVehicle={vehicle}
              vehicleDataChanged={onVehicleDataChanged}
              layoutType="list"
            />
          ))}
        </div>
      )}
      {hasDiscount && specialText && <Discount specialText={specialText} specialColor={specialColor} isSRPPage />}
      {photos && photos?.length > 1 ? (
        <Carousel
          selectedItem={currentSlide}
          onChange={setCurrentSlide}
          swipeable={allowSwipe ? swipeEnabled : undefined}
          preventMovementUntilSwipeScrollTolerance={allowSwipe ? true : undefined}
          swipeScrollTolerance={allowSwipe ? 30 : undefined}
          showStatus={false}
          showThumbs={false}
          showIndicators={false}
          className={"carouselSlider"}
          onClickItem={vehicleClickHandler}
          renderArrowPrev={(clickHandler: () => void, hasPrev) =>
            hasPrev ? (
              <CarCardImageButton
                className={`${styles.arrowContainer} arrow`}
                direction={ArrowDirection.LEFT}
                onClick={clickHandler}
              />
            ) : null
          }
          renderArrowNext={(clickHandler: () => void, hasNext) =>
            hasNext ? (
              <CarCardImageButton
                className={`${styles.arrowContainer} arrow`}
                direction={ArrowDirection.RIGHT}
                onClick={clickHandler}
              />
            ) : null
          }
        >
          {photos.map((item, index) => {
            const isCurrent = currentSlide === index;
            const wasLoaded = loadedSlides.has(index);

            const shouldRender = isCurrent || wasLoaded;

            return shouldRender ? (
              <img
                key={index}
                src={item}
                alt="Car"
                loading={index === 0 ? undefined : "lazy"}
                onLoad={() => markAsLoaded(index)}
                onError={(e) => handleOnImageLoadError(e, vehicle)}
              />
            ) : (
              <div style={{ height: "100%" }} key={index} />
            );
          })}
        </Carousel>
      ) : (
        <img
          src={photos?.[0] || dealerPhoto || `/images/brands/${vehicle?.make.toLowerCase()}.png`}
          alt="Car"
          onError={(e) => handleOnImageLoadError(e, vehicle)}
          loading={undefined}
          onClick={vehicleClickHandler}
        />
      )}
      {!photos?.length && <span className={styles.noImageText}>{t_common("photos_coming_soon")}</span>}
    </div>
  );
};

export default CarCardGalery;
